import { useMedplum } from '@medplum/react-hooks';
import type { Questionnaire, Task } from '@medplum/fhirtypes';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useFhirRealtimeOptional } from '../../../hooks/fhirRealtimeContext';
import { useEffectivePortalPatientId } from '../../../hooks/usePortalSubjectPatientId';
import { readQuestionnaireForResponseRef } from '../../../lib/chart/chartQuestionnaireRef';
import { isClientFacingTask } from '../utils/clientPortalClassifiers';
import { normalizeClientTask } from '../utils/normalizeClientTask';
import type { ClientPortalTaskItem } from '../utils/clientPortalFhirTypes';

const OPEN_TASK_STATUSES = 'requested,received,accepted,ready,in-progress';

export interface UseClientPortalTasksResult {
  tasks: ClientPortalTaskItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

async function resolveQuestionnaireForTask(
  medplum: ReturnType<typeof useMedplum>,
  task: Task,
): Promise<Questionnaire | undefined> {
  const ref = task.focus?.reference;
  if (!ref) return undefined;
  const [rt, id] = ref.split('/');
  if (!id) return undefined;

  if (rt === 'Questionnaire') {
    try {
      return await medplum.readResource('Questionnaire', id);
    } catch {
      return undefined;
    }
  }

  if (rt === 'QuestionnaireResponse') {
    try {
      const qr = await medplum.readResource('QuestionnaireResponse', id);
      const qRef = qr.questionnaire;
      if (!qRef) return undefined;
      return await readQuestionnaireForResponseRef(medplum, qRef);
    } catch {
      return undefined;
    }
  }

  return undefined;
}

export function useClientPortalTasks(): UseClientPortalTasksResult {
  const medplum = useMedplum();
  const patientId = useEffectivePortalPatientId();
  const [tasks, setTasks] = useState<ClientPortalTaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const hasTasksRef = useRef(false);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    hasTasksRef.current = false;
    setTasks([]);
    setLoading(!!patientId);
  }, [patientId]);

  const loadTasks = useCallback(
    async (opts?: { showLoading?: boolean }) => {
      if (!patientId) {
        setTasks([]);
        setLoading(false);
        return;
      }

      const showLoading = opts?.showLoading ?? !hasTasksRef.current;
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        medplum.invalidateSearches('Task');
        const rawTasks = await medplum.searchResources('Task', {
          patient: `Patient/${patientId}`,
          status: OPEN_TASK_STATUSES,
          _sort: '-_lastUpdated',
          _count: '100',
        });

        const clientTasks = rawTasks.filter(isClientFacingTask);

        const normalized = await Promise.all(
          clientTasks.map(async (task) => {
            const questionnaire = await resolveQuestionnaireForTask(medplum, task);
            return normalizeClientTask(task, questionnaire);
          }),
        );

        setTasks(normalized);
        hasTasksRef.current = true;
      } catch (err) {
        console.error('[useClientPortalTasks] Failed to fetch tasks:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tasks');
        setTasks([]);
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [medplum, patientId],
  );

  const loadTasksRef = useRef(loadTasks);
  loadTasksRef.current = loadTasks;

  useEffect(() => {
    void loadTasks({ showLoading: true });
  }, [loadTasks, tick]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const fhirRealtime = useFhirRealtimeOptional();

  useEffect(() => {
    if (!fhirRealtime) return;
    return fhirRealtime.onClientPortalRefresh(() => {
      void loadTasksRef.current({ showLoading: false });
    });
  }, [fhirRealtime]);

  return { tasks, loading, error, refresh };
}
